import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ChevronRight,
  Paintbrush,
  Building2,
  Layers3,
  ShieldCheck,
  Droplets,
  Gem,
  LayoutGrid,
} from "lucide-react";

const HomeProductSection = () => {
  const navigate = useNavigate();

  // =====================================================
  // STATES
  // =====================================================

  const [mainCategories, setMainCategories] = useState([]);
  const [selectedMainCatId, setSelectedMainCatId] = useState(null);

  const [subCategories, setSubCategories] = useState([]);

  const [loading, setLoading] = useState(false);

  // =====================================================
  // ICON MAP
  // =====================================================

  const iconMap = {
    Paintbrush,
    Building2,
    Layers3,
    ShieldCheck,
    Droplets,
    Gem,
    LayoutGrid,
  };

  // =====================================================
  // FETCH MAIN CATEGORY
  // =====================================================

  useEffect(() => {
    fetchMainCategory();
  }, []);

  const fetchMainCategory = async () => {
    try {
      const response = await fetch(
        "https://webadmin.rigve.com/api/get_main_category",
      );

      const result = await response.json();

      if (result.status === 1 && result.data) {
        // ADD ICONS

        const updatedData = result.data.map((item, index) => ({
          ...item,
          icon: [
            "Paintbrush",
            "Building2",
            "Layers3",
            "LayoutGrid",
            "ShieldCheck",
            "Gem",
            "Droplets",
          ][index % 7],
        }));

        setMainCategories(updatedData);

        // DEFAULT ACTIVE CATEGORY

        if (updatedData.length > 0) {
          setSelectedMainCatId(updatedData[0].id);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  // =====================================================
  // FETCH SUB CATEGORY
  // =====================================================

  useEffect(() => {
    if (!selectedMainCatId) return;

    fetchSubCategory();
  }, [selectedMainCatId]);

  const fetchSubCategory = async () => {
    setLoading(true);

    try {
      const response = await fetch(
        `https://webadmin.rigve.com/api/get_category?main_cat_id=${selectedMainCatId}`,
      );

      const result = await response.json();

      if (result.status === 1) {
        setSubCategories(result.data || []);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // ACTIVE CATEGORY
  // =====================================================

  const activeCategory = mainCategories.find(
    (item) => item.id === selectedMainCatId,
  );

  return (
    <section className="py-16 bg-slate-50 overflow-hidden" id="products_home">
      <div className="max-w-7xl mx-auto px-4">
        {/* ===================================================== */}
        {/* HEADING */}
        {/* ===================================================== */}

        <div className="max-w-3xl mx-auto text-center mb-14">
          <h2 className="fs-21 font-bold text-gray-900">
            Our Collection of{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600">
              Products
            </span>
          </h2>

          <div className="w-24 h-1 bg-orange-500 mx-auto my-5 rounded-full"></div>

          <p className="text-gray-600 text-md max-w-xl mx-auto leading-relaxed">
            Premium decorative finishes, seamless flooring, waterproofing,
            textures and innovative surface solutions.
          </p>
        </div>

        {/* ===================================================== */}
        {/* MAIN CATEGORY NAV TAB */}
        {/* ===================================================== */}

        <div className="mt-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {mainCategories.map((item) => {
              const Icon = iconMap[item.icon] || LayoutGrid;

              return (
                <button
                  key={item.id}
                  onClick={() =>
                    window.innerWidth >= 100 && setSelectedMainCatId(item.id)
                  }
                  className={`w-full text-left rounded-2xl
                  transition-all duration-300
                  p-3 border
                  ${
                    selectedMainCatId === item.id
                      ? "bg-orange-500 text-white border-orange-500 shadow-lg"
                      : "bg-white hover:bg-orange-50 text-gray-800 border-gray-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {/* ICON */}

                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center
                      transition-all duration-300
                      ${
                        selectedMainCatId === item.id
                          ? "bg-white/20"
                          : "bg-orange-100 text-orange-500"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>

                    {/* TEXT */}

                    <div className="flex-1 overflow-hidden">
                      <p
                        className="
                        font-semibold
                        line-clamp-1
                        text-sm
                      "
                      >
                        {item.main_category_name || item.name}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ===================================================== */}
        {/* SUB CATEGORY SECTION */}
        {/* ===================================================== */}

        <div className="mt-4">
          {" "}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((item) => (
                <div
                  key={item}
                  className="bg-white rounded-3xl p-4 animate-pulse"
                >
                  <div className="h-[180px] bg-gray-200 rounded-2xl"></div>

                  <div className="mt-4 h-4 bg-gray-200 rounded"></div>

                  <div className="mt-2 h-3 bg-gray-200 rounded"></div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* SUB CATEGORY GRID */}

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {subCategories.map((collection) => {
                  // =====================================================
                  // API FIELD FIX
                  // =====================================================

                  const image =
                    collection.category_image ||
                    collection.image ||
                    collection.photo;

                  const name =
                    collection.category_name ||
                    collection.name ||
                    collection.title;

                  const detail =
                    collection.short_description ||
                    collection.description ||
                    "Premium decorative wall texture collection.";

                  return (
                    <div
                      key={collection.id}
                      onClick={() => {
                        navigate(
                          `/collections/${collection.id}/${selectedMainCatId}`,
                        );
                      }}
                      className="
                      group cursor-pointer
                      border border-gray-100
                      rounded-2xl
                      bg-white
                      
                      hover:border-orange-200
                      hover:bg-orange-50
                      hover:shadow-xl
                      transition-all duration-300
                    "
                    >
                      {/* IMAGE */}

                      <div
                        className="
                        h-[120px] sm:h-[140px] md:h-[180px]
                        w-full
                        rounded-t-2xl
                        overflow-hidden
                        bg-gray-100 mb-3"
                      >
                        <img
                          src={image}
                          alt={name}
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/400x300?text=No+Image";
                          }}
                          className="
                          w-full h-full object-cover
                          group-hover:scale-105
                          transition-all duration-500
                        "
                        />
                      </div>

                      {/* CONTENT */}

                      <div className="px-4 pb-4">
                        {/* NAME */}

                        <h3
                          className="
                          font-bold
                          text-sm md:text-lg
                          text-gray-900
                          group-hover:text-orange-600
                          transition-all duration-300
                          line-clamp-1
                        "
                        >
                          {name}
                        </h3>

                        {/* DETAIL */}

                        <p
                          className="
                          text-xs md:text-sm
                          text-gray-500
                          mt-2
                          leading-relaxed
                          line-clamp-2
                        "
                        >
                          {detail}
                        </p>

                        {/* BUTTON */}

                        <div
                          className="
                          mt-4
                          inline-flex items-center gap-2
                          text-orange-500
                          font-semibold
                          text-xs md:text-sm
                        "
                        >
                          Explore Collection
                          <ChevronRight
                            className="
                            w-4 h-4
                            transition-all duration-300
                            group-hover:translate-x-1
                          "
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* EMPTY */}

              {subCategories.length === 0 && (
                <div className="text-center py-20">
                  <h3 className="text-3xl font-bold text-gray-700">
                    No Collections Found
                  </h3>

                  <p className="text-gray-500 mt-3">
                    Collections will appear here soon.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default HomeProductSection;
